/**
 * Session-only attribute-table workspace helpers: sort, hide, freeze,
 * and column order. Kept out of TSX so node/SSR tests can cover the
 * behaviour without a DOM.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export type SortDirection = 'asc' | 'desc';

export interface SortLevel {
  readonly field: string;
  readonly direction: SortDirection;
}

export type WorkspaceTabKind = 'table' | 'selected' | 'chart' | 'summary';

export interface TableWorkspaceTab {
  readonly id: string;
  readonly kind: WorkspaceTabKind;
  readonly label: string;
  readonly column?: string;
  readonly summaryColumns?: ReadonlyArray<string>;
  readonly summaryRows?: ReadonlyArray<VectorExportFeature>;
}

export const TABLE_TAB_ID = 'table';
export const SELECTED_RECORDS_TAB_ID = 'selected-records';

export const TABLE_WORKSPACE_TAB: TableWorkspaceTab = {
  id: TABLE_TAB_ID,
  kind: 'table',
  label: 'Attribute table',
};

export function createSelectedRecordsTab(count: number): TableWorkspaceTab {
  return {
    id: SELECTED_RECORDS_TAB_ID,
    kind: 'selected',
    label: `Selected Records (${count})`,
  };
}

export function cloneAttributeRows<T extends VectorExportFeature>(
  rows: ReadonlyArray<T>,
): T[] {
  return rows.map((row, index) => ({
    ...row,
    properties: { ...(row.properties ?? {}) },
    _rowKey: attributeRowKey(row, index),
  }));
}

export function attributeRowKey(
  row: VectorExportFeature,
  index = 0,
): string {
  const tagged = row as VectorExportFeature & { readonly _rowKey?: string };
  if (tagged._rowKey) return tagged._rowKey;
  return `row-${index}-${String(row.id ?? 'x')}`;
}

export function toggleSelectedRowKey(
  selected: ReadonlySet<string>,
  key: string,
  force?: boolean,
): Set<string> {
  const next = new Set(selected);
  const shouldSelect = force ?? !next.has(key);
  if (shouldSelect) next.add(key);
  else next.delete(key);
  return next;
}

export function selectedKeysForRows(
  rows: ReadonlyArray<VectorExportFeature>,
): Set<string> {
  return new Set(rows.map((row, index) => attributeRowKey(row, index)));
}

export function invertSelectedRowKeys(
  rows: ReadonlyArray<VectorExportFeature>,
  selected: ReadonlySet<string>,
): Set<string> {
  const next = new Set<string>();
  rows.forEach((row, index) => {
    const key = attributeRowKey(row, index);
    if (!selected.has(key)) next.add(key);
  });
  return next;
}

export function filterRowsBySelectedKeys<T extends VectorExportFeature>(
  rows: ReadonlyArray<T>,
  selected: ReadonlySet<string>,
): T[] {
  return rows.filter((row, index) => selected.has(attributeRowKey(row, index)));
}

export function sourceFingerprint(rows: ReadonlyArray<VectorExportFeature>): string {
  if (rows.length === 0) return '0';
  const first = rows[0];
  const last = rows[rows.length - 1];
  return `${rows.length}:${String(first?.id)}:${String(last?.id)}`;
}

export function sameStringList(
  left: ReadonlyArray<string>,
  right: ReadonlyArray<string>,
): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

export function compareCellValues(
  left: unknown,
  right: unknown,
  direction: SortDirection,
): number {
  const leftEmpty = isEmpty(left);
  const rightEmpty = isEmpty(right);
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  const leftNumber = asFiniteNumber(left);
  const rightNumber = asFiniteNumber(right);
  let raw = 0;
  if (leftNumber !== null && rightNumber !== null) {
    raw = leftNumber - rightNumber;
  } else {
    raw = stringifyCell(left).localeCompare(stringifyCell(right), undefined, {
      sensitivity: 'base',
      numeric: true,
    });
  }
  if (raw === 0) return 0;
  return direction === 'asc' ? (raw < 0 ? -1 : 1) : raw < 0 ? 1 : -1;
}

export function sortAttributeRows<T extends VectorExportFeature>(
  rows: ReadonlyArray<T>,
  spec: ReadonlyArray<SortLevel>,
): T[] {
  const levels = spec.filter((level) => level.field.length > 0);
  if (levels.length === 0) return [...rows];
  return [...rows].sort((left, right) => {
    for (const level of levels) {
      const cmp = compareCellValues(
        left.properties?.[level.field],
        right.properties?.[level.field],
        level.direction,
      );
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
}

export function singleColumnSort(field: string, direction: SortDirection): SortLevel[] {
  return [{ field, direction }];
}

export function hideColumn(
  visible: ReadonlyArray<string>,
  column: string,
): string[] {
  if (visible.length <= 1) return [...visible];
  return visible.filter((name) => name !== column);
}

export function canHideColumn(visibleCount: number): boolean {
  return visibleCount > 1;
}

export function visibleColumns(
  order: ReadonlyArray<string>,
  hidden: ReadonlySet<string>,
): string[] {
  return order.filter((name) => !hidden.has(name));
}

export function toggleHiddenColumn(
  hidden: ReadonlySet<string>,
  column: string,
  order: ReadonlyArray<string>,
): Set<string> {
  const next = new Set(hidden);
  if (next.has(column)) {
    next.delete(column);
    return next;
  }
  if (visibleColumns(order, next).length <= 1) return next;
  next.add(column);
  return next;
}

export function mergeColumnOrder(
  current: ReadonlyArray<string>,
  incoming: ReadonlyArray<string>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of current) {
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  for (const name of incoming) {
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

export function moveColumn(
  order: ReadonlyArray<string>,
  column: string,
  delta: -1 | 1,
): string[] {
  const index = order.indexOf(column);
  if (index < 0) return [...order];
  const nextIndex = index + delta;
  if (nextIndex < 0 || nextIndex >= order.length) return [...order];
  const out = [...order];
  const [removed] = out.splice(index, 1);
  out.splice(nextIndex, 0, removed);
  return out;
}

export function freezeThrough(
  visible: ReadonlyArray<string>,
  column: string,
): string[] {
  const index = visible.indexOf(column);
  if (index < 0) return [];
  return visible.slice(0, index + 1);
}

export function frozenLeftOffset(
  frozen: ReadonlyArray<string>,
  column: string,
  columnWidth: number,
): number | null {
  const index = frozen.indexOf(column);
  if (index < 0) return null;
  return index * columnWidth;
}

export function createChartTab(column: string): TableWorkspaceTab {
  return {
    id: `chart-${column}-${stableId(column)}`,
    kind: 'chart',
    label: `Counts by ${column}`,
    column,
  };
}

export function createSummaryTab(
  groupBy: string,
  columns: ReadonlyArray<string>,
  rows: ReadonlyArray<VectorExportFeature>,
): TableWorkspaceTab {
  return {
    id: `summary-${groupBy}-${rows.length}`,
    kind: 'summary',
    label: `Summary by ${groupBy}`,
    column: groupBy,
    summaryColumns: columns,
    summaryRows: rows,
  };
}

export function closeWorkspaceTab(
  tabs: ReadonlyArray<TableWorkspaceTab>,
  tabId: string,
  activeId: string,
): { tabs: TableWorkspaceTab[]; activeId: string } {
  if (tabId === TABLE_TAB_ID) {
    return { tabs: [...tabs], activeId };
  }
  const next = tabs.filter((tab) => tab.id !== tabId);
  const nextActive = activeId === tabId ? TABLE_TAB_ID : activeId;
  return { tabs: next, activeId: nextActive };
}

export function upsertWorkspaceTab(
  tabs: ReadonlyArray<TableWorkspaceTab>,
  tab: TableWorkspaceTab,
): TableWorkspaceTab[] {
  const existing = tabs.findIndex((entry) => entry.id === tab.id);
  if (existing < 0) return [...tabs, tab];
  const next = [...tabs];
  next[existing] = tab;
  return next;
}

export const HEADER_ACTION_HINT = 'Double-click to perform actions';

export function createEmptyAttributeRow(
  columns: ReadonlyArray<string>,
  id: string | number,
): VectorExportFeature & { readonly _rowKey: string } {
  const properties: Record<string, unknown> = {};
  for (const column of columns) properties[column] = null;
  const rowKey = String(id);
  return { id, properties, _rowKey: rowKey };
}

export function nextAddedRowId(rows: ReadonlyArray<VectorExportFeature>): string {
  return `added-${rows.length + 1}`;
}

export function updateAttributeCell(
  rows: ReadonlyArray<VectorExportFeature>,
  target: VectorExportFeature,
  column: string,
  value: unknown,
): VectorExportFeature[] {
  const targetKey = (target as { readonly _rowKey?: string })._rowKey;
  return rows.map((row, index) => {
    const rowKey = (row as { readonly _rowKey?: string })._rowKey;
    const matches = row === target || Boolean(targetKey && rowKey === targetKey);
    if (!matches) return row;
    return {
      ...row,
      _rowKey: rowKey ?? attributeRowKey(row, index),
      properties: {
        ...(row.properties ?? {}),
        [column]: value,
      },
    };
  });
}

export function parseEditedCellValue(raw: string, previous: unknown): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (typeof previous === 'boolean') {
    if (trimmed.toLowerCase() === 'true') return true;
    if (trimmed.toLowerCase() === 'false') return false;
  }
  if (typeof previous === 'number' || previous === null || previous === undefined) {
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && trimmed === String(parsed)) return parsed;
    if (typeof previous === 'number' && Number.isFinite(Number(trimmed))) return Number(trimmed);
  }
  return raw;
}

export function cellInputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function asFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function stringifyCell(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  try {
    return JSON.stringify(value) ?? '';
  } catch {
    return String(value);
  }
}

function stableId(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}
